<template>
  <v-app id="dashboard">
    <navigation></navigation>

    <v-main class="grey lighten-3">
      <v-container>
        <v-row>

          <!-- FORM -->
          <v-col cols="md-6">
            <v-sheet rounded outlined class="pa-6" min-height="100%">

              <v-form ref="workForm" v-model="valid">
                <v-row v-if="workFormData">

                  <v-col cols="12" class="pb-0">
                    <div class="work-editor-section-label text-caption grey--text text--darken-2 mb-4">
                      Produktdaten
                    </div>
                  </v-col>

                  <!-- Image -->
                  <v-col cols="12">
                    <v-file-input
                        label="Bild"
                        accept="image/png, image/jpeg, image/gif"
                        prepend-icon="mdi-image"
                        persistent-hint
                        hint="Erlaubt: PNG, JPEG, GIF · maximale Dateigröße 2 MB, max 1024x1024px"
                        :rules="imageRules"
                        @change="onImageChange"
                    />
                  </v-col>

                  <!-- Name -->
                  <v-col cols="12">
                    <v-text-field
                        v-model="workFormData.name"
                        label="Produktname"
                        :rules="[rules.required, rules.max200]"
                        counter="200"
                    />
                  </v-col>

                  <!-- Description -->
                  <v-col cols="12">
                    <v-textarea
                        v-model="workFormData.description"
                        label="Beschreibung"
                        :rules="[rules.max1000]"
                        counter="1000"
                    />
                  </v-col>

                  <!-- Location -->
                  <v-col cols="12">
                    <v-text-field
                        v-model="workFormData.location"
                        label="Ort / Link"
                        :rules="[rules.max200]"
                        counter="200"
                    >
                      <template v-slot:prepend-inner>
                        <v-select
                            v-model="locationType"
                            :items="['Ort', 'https://']"
                            dense
                            hide-details
                            solo
                            flat
                            style="max-width: 110px"
                        />
                      </template>
                    </v-text-field>
                  </v-col>

                  <!-- Price -->
                  <v-col cols="6">
                    <v-text-field
                        v-model.number="workFormData.price"
                        label="Preis"
                        type="number"
                        :rules="[rules.numeric]"
                    />
                  </v-col>

                  <!-- Currency -->
                  <v-col cols="6">
                    <v-select
                        v-model="workFormData.currency"
                        :items="currencies"
                        label="Währung"
                    />
                  </v-col>

                  <!-- Price spec -->
                  <v-col cols="6">
                    <v-select
                        v-model="workFormData.price_spec"
                        :items="priceSpecs"
                        item-text="label"
                        item-value="value"
                        label="Preisangabe"
                    />
                  </v-col>

                  <!-- Discount -->
                  <v-col cols="6">
                    <v-text-field
                        v-model.number="workFormData.discount"
                        label="Rabatt (%)"
                        type="number"
                        :rules="[rules.discount]"
                    />
                  </v-col>

                  <v-col cols="12">
                    <div class="work-editor-meta pa-4 mb-4">
                      <div class="work-editor-section-label text-caption grey--text text--darken-2 mb-4">
                        Metadaten
                      </div>
                      <v-row>
                        <v-col cols="12">
                          <v-text-field
                              v-model="workFormData.internal_name"
                              label="Interner Name"
                              :rules="[rules.required, rules.max200]"
                              counter="200"
                          />
                        </v-col>
                        <v-col cols="4">
                          <v-select
                              v-model="workFormData.status"
                              :items="statuses"
                              item-text="text"
                              item-value="value"
                              label="Status"
                              :rules="[rules.required]"
                          />
                        </v-col>
                        <v-col cols="4">
                          <v-menu
                              v-model="menuValidFrom"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y
                              min-width="auto"
                          >
                            <template #activator="{ on, attrs }">
                              <v-text-field
                                  :value="formatDateDe(workFormData.valid_from)"
                                  label="Gültig von"
                                  readonly
                                  clearable
                                  prepend-inner-icon="mdi-calendar"
                                  v-bind="attrs"
                                  v-on="on"
                                  :rules="validFromRules"
                                  @click:clear.stop="clearValidFrom"
                              />
                            </template>
                            <v-date-picker
                                v-model="workFormData.valid_from"
                                locale="de"
                                :first-day-of-week="1"
                                scrollable
                                @input="menuValidFrom = false"
                            />
                          </v-menu>
                        </v-col>
                        <v-col cols="4">
                          <v-menu
                              v-model="menuValidTo"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y
                              min-width="auto"
                          >
                            <template #activator="{ on, attrs }">
                              <v-text-field
                                  :value="formatDateDe(workFormData.valid_to)"
                                  label="Gültig bis"
                                  readonly
                                  clearable
                                  prepend-inner-icon="mdi-calendar"
                                  v-bind="attrs"
                                  v-on="on"
                                  :rules="validToRules"
                                  @click:clear.stop="clearValidTo"
                              />
                            </template>
                            <v-date-picker
                                v-model="workFormData.valid_to"
                                locale="de"
                                :first-day-of-week="1"
                                scrollable
                                @input="menuValidTo = false"
                            />
                          </v-menu>
                        </v-col>
                      </v-row>
                    </div>
                  </v-col>

                  <!-- Back, Delete, Save -->
                  <v-row class="mx-0">
                    <v-col cols="4">
                      <v-btn :to="{ name: 'marketing' }">
                        Zurück
                      </v-btn>
                    </v-col>
                    <v-col cols="4" style="text-align: center">
                      <v-btn v-if="!create" @click="destroy">
                        Löschen
                      </v-btn>
                    </v-col>
                    <v-col cols="4" style="text-align: right">
                      <v-btn color="primary" @click="save">
                        Speichern
                      </v-btn>
                    </v-col>
                  </v-row>

                </v-row>
              </v-form>

            </v-sheet>
          </v-col>

          <!-- PREVIEW -->
          <v-col cols="md-6">
            <v-sheet min-height="100%" rounded outlined class="pa-6" style="background: none; border: none; margin-top: 0 !important; padding-top: 0 !important;">

              <div style="display:flex; justify-content:center; padding:0 40px; background:#eee;">
                <div style="
      width:460px;
      height:980px;
      background:#111;
      border-radius:40px;
      padding:20px;
      box-shadow:0 10px 30px rgba(0,0,0,0.3);
      position:relative;
  ">

                  <!-- optional top notch -->
                  <div style="
        position:absolute;
        top:10px;
        left:50%;
        transform:translateX(-50%);
        width:120px;
        height:20px;
        background:#222;
        border-radius:10px;
    "></div>

                  <!-- screen -->
                  <div style="
        width:100%;
        height:100%;
        background:#000;
        border-radius:30px;
        overflow:hidden;
    ">
                    <div class="show-preview-loading" v-if="showPreviewLoading">
                      <div class="loader"></div>
                    </div>
                    <iframe
                        :src="profileUrl"
                        style="width:100%; height:100%; border:none;">
                    </iframe>
                  </div>

                </div>
              </div>

            </v-sheet>
          </v-col>

        </v-row>

        <v-snackbar
            v-model="snackbar.show"
            :timeout="snackbar.timeout"
            :color="snackbar.color"
            top
            right
        >
          {{ snackbar.text }}
          <v-btn text @click="snackbar.show = false">Schließen</v-btn>
        </v-snackbar>
      </v-container>
    </v-main>
  </v-app>
</template>
<style scoped>
.work-editor-meta {
  background-color: #fafafa;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-left: 3px solid #b0bec5;
}

.work-editor-section-label {
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.show-preview-loading {
  position: absolute;
  top: 30px;
  right: 30px;
  padding: 12px;
}

.loader {
  border: 8px solid #fff;
  border-top: 8px solid #ff1010;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

</style>
<script>
import Navigation from "./Navigation.vue";
import moment from "moment/moment";
import WorkPreview from "@/components/WorkPreview.vue";
import debounce from "lodash.debounce";
import axios from "axios";

export default {
  components: {Navigation, WorkPreview},

  data() {
    return {
      valid: false,
      token: null,

      imagePreview: null,
      imageChanged: false,
      imageFile: null,
      create: true,
      showPreviewLoading: false,
      menuValidFrom: false,
      menuValidTo: false,
      locationType: "https://",

      workFormData: {
        internal_name: "",
        name: "",
        description: "",
        location: "",
        price: null,
        currency: "EUR",
        price_spec: null,
        discount: null,
        image: null,
        valid_from: null,
        valid_to: null,
        status: "DRAFT",
      },

      snackbar: {
        show: false,
        text: '',
        color: 'success',
        timeout: 3000,
      },

      statuses: [
        { text: "Entwurf", value: "DRAFT" },
        { text: "Öffentlich", value: "PUBLIC" },
      ],

      currencies: ["EUR", "CHF", "GBP", "USD"],

      selected_user: null,

      priceSpecs: [
        { value: null, label: "—" },
        { value: "1", label: "schon ab" },
        { value: "3", label: "pro Stunde" },
        { value: "2", label: "Pauschale" },
        { value: "4", label: "auf Anfrage" },
      ],

      rules: {
        required: v => !!v || "Pflichtfeld",
        max200: v => !v || v.length <= 200 || "Höchstens 200 Zeichen",
        max1000: v => !v || v.length <= 1000 || "Höchstens 1000 Zeichen",
        numeric: v => v === null || v === "" || !isNaN(v) || "Bitte eine Zahl eingeben",
        discount: v => (v === null || (v >= 0 && v <= 100)) || "Rabatt muss zwischen 0 und 100 liegen",
        date: v => !v || !isNaN(Date.parse(v)) || "Ungültiges Datum",
      },

      from_date: moment()
          .startOf("isoWeek")
          .format("YYYY-MM-DD"),
      to_date: moment()
          .endOf("isoWeek")
          .format("YYYY-MM-DD"),

      imageRules: [
        file =>
            !file ||
            file.size <= 2048 * 1024 ||
            "Maximal 2 MB",
      ],
    }
  },

  async created() {
    await this.getToken();
    this.debouncedUpdateIframe = debounce(this.updateIframe, 800); // 800ms delay
  },

  computed: {
    users() {
      return this.$store.getters.members;
    },
    assignees() {
      return this.$store.getters.assignees;
    },
    profileUrl() {
      const user_id = localStorage.getItem('user-code');
      return process.env.VUE_APP_PUBLIC_URL + `profile/${user_id}?preview=1&token=${this.token}`;
    },
    validFromRules() {
      return [
        () => {
          const v = this.workFormData.valid_from;
          return !v || !isNaN(Date.parse(v)) || "Ungültiges Datum";
        },
        () => {
          const v = this.workFormData.valid_from;
          if (!v || !this.workFormData.valid_to) return true;
          return moment(v).isSameOrBefore(this.workFormData.valid_to, "day")
            || "„Gültig von“ muss vor oder am selben Tag wie „Gültig bis“ liegen";
        },
      ];
    },
    validToRules() {
      return [
        () => {
          const v = this.workFormData.valid_to;
          return !v || !isNaN(Date.parse(v)) || "Ungültiges Datum";
        },
        () => {
          const v = this.workFormData.valid_to;
          if (!v || !this.workFormData.valid_from) return true;
          return moment(v).isSameOrAfter(this.workFormData.valid_from, "day")
            || "„Gültig bis“ muss nach oder am selben Tag wie „Gültig von“ liegen";
        },
      ];
    },
  },

  watch: {
    work: {
      immediate: true,
      handler(val) {
        if (!val) return;
        this.imagePreview = val.image || null;
      },
    },
    workFormData: {
      deep: true,
      handler() {
        this.debouncedUpdateIframe();
      }
    },
    imageChanged: {
      deep: false,
      handler() {
        this.debouncedUpdateIframe();
      }
    },
    "workFormData.valid_from"() {
      if (this.$refs.workForm) this.$refs.workForm.validate();
    },
    "workFormData.valid_to"() {
      if (this.$refs.workForm) this.$refs.workForm.validate();
    },
    /*
    locationType() {
      this.workFormData.location = '';
    },
     */
    'workFormData.location'(value) {
      if(!this.workFormData || !value) {
        return ;
      }
      if (this.locationType !== 'https://') {
        return;
      }
      this.workFormData.location = value.replace(/^https?:\/\//i, '');
    }

  },

  async mounted() {
    if (this.$route.params.id) {
      this.create = false;
      const res = await this.$store.dispatch("getWork", {
        id: this.$route.params.id,
      });

      // clone into local state
      this.workFormData = { ...this.$store.getters.work };
      this.workFormData.valid_from = this.normalizeDateIso(this.workFormData.valid_from);
      this.workFormData.valid_to = this.normalizeDateIso(this.workFormData.valid_to);
    }

    if (this.workFormData && this.workFormData.location) {
      let loc = this.workFormData.location.trim();

      if (loc.startsWith('http://')) {
        loc = loc.replace('http://', '');
      } else if (loc.startsWith('https://')) {
        loc = loc.replace('https://', '');
      } else {
        this.locationType = 'Ort';
      }

      this.workFormData.location = loc;
    }

    let date_range = {from: this.from_date, to: this.to_date};
    await this.$store.dispatch("getTeam", date_range);
  },

  methods: {
    normalizeDateIso(val) {
      if (val == null || val === "") return null;
      const s = String(val);
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
      const m = moment(s);
      return m.isValid() ? m.format("YYYY-MM-DD") : null;
    },
    formatDateDe(val) {
      if (val == null || val === "") return "";
      let m = moment(val, "YYYY-MM-DD", true);
      if (!m.isValid()) m = moment(val);
      return m.isValid() ? m.format("DD.MM.YYYY") : "";
    },
    clearValidFrom() {
      this.workFormData.valid_from = null;
      this.menuValidFrom = false;
    },
    clearValidTo() {
      this.workFormData.valid_to = null;
      this.menuValidTo = false;
    },
    onImageChange(file) {
      if (!file) return;

      this.imageFile = file;

      const reader = new FileReader();
      reader.onload = e => {
        this.imagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
      this.imageChanged = true;
    },

    async save() {
      if (!this.$refs.workForm.validate()) return;

      try {
        let payload = {
          ...this.workFormData,
          // image: this.imageFile,
        };

        delete payload.image; // to be sent separately

        if(this.locationType==='https://') {
          payload.location = 'https://' + payload.location;
        }

        const res = this.create ?
            await this.$store.dispatch('createWork', {payload: payload}) :
            await this.$store.dispatch('saveWork', {id: payload.id, payload: payload})

        // if (this.imageChanged) {
          const formData = new FormData();
          if (this.imageFile) {
            formData.append('image', this.imageFile);
          }
          await this.$store.dispatch('saveWorkImage', {id: res.data.data.id, formData: formData})
        // }
        this.showToast("Produkt erfolgreich gespeichert.", "success");
      } catch (err) {
        console.error(err);
        this.showToast("Speichern fehlgeschlagen.", "error");
      }

    },

    async destroy() {
      const res = await this.$store.dispatch('deleteWork', {id: this.workFormData.id })
      if(res && res.data && res.data.status==='success') {
        this.showToast("Element gelöscht", "error");
        this.$router.push({ name: 'marketing' });
      } else {
        console.log(res.data);
      }
    },

    priceSpecLabel(value) {
      const found = this.priceSpecs.find(p => p.value == value);
      return found ? found.label : "";
    },

    getFullName(item) {
      if (item.first_name && item.last_name) {
        return item.first_name + " " + item.last_name;
      } else {
        return item.name;
      }
    },

    showToast(message, color = 'success') {
      this.snackbar.text = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    updateIframe() {
      this.flashPreview();

      const iframe = this.$el.querySelector("iframe");

      if (!iframe || !iframe.contentWindow) return;

      const payload = {
        ...this.workFormData
      };

      // todo - only send image if changed?
      // if (this.imageChanged) {
        payload.image_base64 = this.imagePreview;
      // }

      // this.showToast('Updating preview');

      iframe.contentWindow.postMessage(
          {
            type: "WORK_PREVIEW",
            payload: payload
          },
          "*"
      );

      // reset flag after sending
      this.imageChanged = false;
    },

    flashPreview() {
      this.showPreviewLoading = true;
      setTimeout(() => {
        this.showPreviewLoading = false;
      }, 1000); // 1 second
    },

    async getToken() {
      const res = await this.$store.dispatch("getToken");
      this.token = res.data;
    }

  },
};
</script>
